import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Grid,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const STATUS_OPTIONS = [
  { label: 'Not Started', value: 'pending' },
  { label: 'In Progress', value: 'in-progress' },
  { label: 'Completed', value: 'completed' },
  { label: 'On Hold', value: 'on-hold' },
];

const AddTaskClassificationModal = ({ open, onClose, onSubmit, classification, projectId, isEditMode }) => {
  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    status: Yup.string().required('Status is required'),
  });

  const initialValues = {
    name: classification?.name || '',
    plannedStartDate: classification?.plannedStartDate ? dayjs(classification.plannedStartDate) : null,
    plannedEndDate: classification?.plannedEndDate ? dayjs(classification.plannedEndDate) : null,
    actualStartDate: classification?.actualStartDate ? dayjs(classification.actualStartDate) : null,
    actualEndDate: classification?.actualEndDate ? dayjs(classification.actualEndDate) : null,
    remark: classification?.remark || '',
    status: classification?.status || 'pending',
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        id: classification?.id,
        projectId,
        name: values.name,
        plannedStartDate: values.plannedStartDate ? values.plannedStartDate.format('YYYY-MM-DD') : null,
        plannedEndDate: values.plannedEndDate ? values.plannedEndDate.format('YYYY-MM-DD') : null,
        actualStartDate: values.actualStartDate ? values.actualStartDate.format('YYYY-MM-DD') : null,
        actualEndDate: values.actualEndDate ? values.actualEndDate.format('YYYY-MM-DD') : null,
        remark: values.remark,
        status: values.status,
      };

      await onSubmit(payload);
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Task Classification' : 'Add Task Classification'}</DialogTitle>

      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} mt={1}>
            {/* NAME */}
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Name"
                fullWidth
                size="small"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Grid>

            {/* PLANNED START */}
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Planned Start Date"
                value={formik.values.plannedStartDate}
                onChange={(v) => formik.setFieldValue('plannedStartDate', v)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField {...params} size="small" fullWidth helperText="" />}
              />
            </Grid>

            {/* PLANNED END */}
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Planned End Date"
                value={formik.values.plannedEndDate}
                onChange={(v) => formik.setFieldValue('plannedEndDate', v)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField {...params} size="small" fullWidth helperText="" />}
              />
            </Grid>

            {/* ACTUAL START */}
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Actual Start Date"
                value={formik.values.actualStartDate}
                onChange={(v) => formik.setFieldValue('actualStartDate', v)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField {...params} size="small" fullWidth helperText="" />}
              />
            </Grid>

            {/* ACTUAL END */}
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Actual End Date"
                value={formik.values.actualEndDate}
                onChange={(v) => formik.setFieldValue('actualEndDate', v)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField {...params} size="small" fullWidth helperText="" />}
              />
            </Grid>

            {/* REMARK */}
            <Grid item xs={12}>
              <TextField
                name="remark"
                label="Remark"
                fullWidth
                multiline
                minRows={2}
                size="small"
                value={formik.values.remark}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="status"
                label="Status"
                fullWidth
                size="small"
                value={formik.values.status}
                onChange={formik.handleChange}
                SelectProps={{ native: true }}
                error={formik.touched.status && Boolean(formik.errors.status)}
                helperText={formik.touched.status && formik.errors.status}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={formik.handleSubmit} color="primary">
          {formik.isSubmitting ? <CircularProgress size={22} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTaskClassificationModal;
